import LoadingButton from '@mui/lab/LoadingButton';
import { Button, ButtonGroup, Grid, Stack, Typography } from "@mui/material";
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, MutationDefinition, QueryDefinition } from "@reduxjs/toolkit/dist/query";
import { UseMutation, UseQuery } from "@reduxjs/toolkit/dist/query/react/buildHooks";
import { FieldArray, Form, Formik, FormikProps, FormikValues } from "formik";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Error } from "../../models/API/Error";
import { RequestMutationWrapper } from "../../models/API/RequestMutationWrapper";
import { ConfirmationButton } from "../ConfirmationButton";
import ErrorCodeHook from "../DTO/ErrorCodeHook";
import Loading from "../loading";
import EasyFormColumn from "./EasyFormColumn";
import { EasyFormRow, EasyFormSideBySide } from "./FormLayout";
import { FieldEditorRender } from "./Renders/FieldEditorRender";

export enum FormType {
    INFORMATION,
    LIST,
}

export interface EasyFieldProps {
    values: any
    touched: any
    errors: any
    handleChange: any
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
    minWidth: string | number
    submitCount: number
}

export interface EasyFormProps<CustomType> {
    SignupSchema: any
    idToFetch?: string
    createNew: () => CustomType
    ressourceOwnerId?: string //If we are about to update an element in a collection owned by a specific fx organisation we set the ressourceOwnerId to the organisation-id
    editable?: boolean
    isDeleteable?: boolean
    editModeAlways?: boolean
    showResetButton?: boolean
    arrayPath: string
    isAllowedToEdit: (entity: CustomType) => boolean
    columns: EasyFormColumn[]
    errorToMutation?: ErrorCodeHook<CustomType>[]
    formType: FormType
    extraButtons?: JSX.Element
    newButtonTooltip?: string
    initialValue?: CustomType
    saveMutation: UseMutation<MutationDefinition<RequestMutationWrapper<CustomType>, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>, any, any, any>>
    additionalSaveActions?: (request?: RequestMutationWrapper<CustomType>) => Promise<any> | undefined
    getByIdAction?: UseQuery<QueryDefinition<string, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>, any, CustomType, any>>
    afterSaveAction?: (value?: CustomType) => void
    getFieldRows: (value: CustomType) => Array<FieldEditorRender[]>
    saveConfirmationBody?: (fieldValues: CustomType) => JSX.Element | undefined
    displayTextWidth?: string
}


export default function EasyForm<CustomType>(props: EasyFormProps<CustomType>) {
    let value: CustomType | undefined = props.initialValue
    const showResetButton = props.showResetButton ?? false;

    let isFetchingValue = false
    if (props.initialValue == undefined && props.getByIdAction && props.idToFetch) {
        const { data, isLoading } = props.getByIdAction(props.idToFetch)
        value = data;
        isFetchingValue = isLoading
    }


    const { t } = useTranslation()
    const [editModeTemp, setEditModeTemp] = useState(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [loadingSave, setLoadingSave] = useState<boolean>(false);
    const editMode = props.editModeAlways ?? editModeTemp;

    const errorCodeHook: ErrorCodeHook<CustomType> | undefined = error != undefined || props.errorToMutation ? props.errorToMutation?.find(errorCodeHook => errorCodeHook.errorCode == error?.error_code) : undefined;
    const mutation = errorCodeHook?.hook ?? props.saveMutation
    const [mutationAction] = mutation()

    const saveFunction = async (request: RequestMutationWrapper<CustomType>) => {
        setLoadingSave(true)

        const saveWithAdditionalActions = async (request: RequestMutationWrapper<CustomType>) => {
            const returnValue = await mutationAction(request).unwrap()
            await props.additionalSaveActions?.(request)
            return returnValue
        }

        await saveWithAdditionalActions(request).then(returnValue => {
            setEditModeTemp(false)
            props.afterSaveAction?.(returnValue);
        }).catch((error: { data: Error }) => {
            setError(error.data);
        }).finally(() => {
            setLoadingSave(false)
        })
    }

    const [newValue, setNewValue] = useState<CustomType | undefined>();

    useMemo(() => {
        if (isFetchingValue)
            return (<Loading />)

        if (value == undefined) {
            setNewValue(props.createNew());
            value = newValue;
        }
    }, [])

    value = value ?? newValue;

    const isAllowedToEdit = props.isAllowedToEdit(value!);
    return (
        <Formik
            key={JSON.stringify(value)}
            initialValues={value as FormikValues}
            validationSchema={props.SignupSchema}
            onSubmit={async (values) => {
                await saveFunction({ parentId: props.ressourceOwnerId, data: values as CustomType })
            }}
        >

            {(formikProps) => {

                return (
                    <Form
                        onResetCapture={() => setEditModeTemp(false)}
                    >
                        <FieldArray
                            name={props.arrayPath}
                            render={arrayHelpers => (
                                <Grid spacing={10} columns={props.columns.length} container>
                                    {props.formType == FormType.INFORMATION ? <EasyFormSideBySide displayTextWidth={props.displayTextWidth} {...formikProps} setEditModeTemp={setEditModeTemp} columns={props.columns} editMode={editMode} /> : <></>}
                                    {props.formType == FormType.LIST ? <EasyFormRow confirmDeletionText={"Are you sure you wish to delete this entity?"}  {...formikProps as unknown as FormikProps<CustomType>} setEditModeTemp={setEditModeTemp} editMode={editMode} {...props} arrayHelpers={arrayHelpers} /> : <></>}
                                </Grid>
                            )
                            }
                        />
                        {(props.editable == undefined || props.editable) && isAllowedToEdit ?
                            <Stack direction="row" paddingTop={2}>
                                {
                                    editMode ?
                                        <>
                                            <ButtonGroup>
                                                {error != undefined && errorCodeHook != undefined ?
                                                    <ConfirmationButton action={async () => { formikProps.handleSubmit() }} type="submit" variant="contained" title={t(errorCodeHook.message)} buttonText={t("Force save")}>
                                                        <Stack>
                                                            <Typography>{t(errorCodeHook.confirmMessage) + " "}</Typography>
                                                        </Stack>
                                                    </ConfirmationButton> :
                                                    props.saveConfirmationBody?.(formikProps.values as CustomType) ?
                                                        <ConfirmationButton color="primary" action={async () => { formikProps.handleSubmit() }} type="submit" variant="contained" title="" buttonText={t("Save")} loading={loadingSave}>
                                                            {props.saveConfirmationBody(formikProps.values as CustomType)}
                                                        </ConfirmationButton>
                                                        :
                                                        <LoadingButton variant="contained" loading={loadingSave} type="submit" >
                                                            <>{t("Save") + ""}</>
                                                        </LoadingButton>
                                                }
                                                {showResetButton ? <Button type="reset">{t("Reset") + ""}</Button> : <></>}
                                                {props.extraButtons ?? <></>}
                                            </ButtonGroup>
                                        </>
                                        : <ButtonGroup>
                                            <Button variant="contained" type="button" onClick={() => setEditModeTemp(true)}>{t("Edit") + ""}</Button>
                                        </ButtonGroup>
                                }
                            </ Stack>
                            : <></>
                        }
                    </Form >)
            }
            }


        </Formik >
    )
}